import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, AbstractControl, Validators } from '@angular/forms';
import { merge, Subject, BehaviorSubject } from 'rxjs';
import { startWith, map, tap, switchMap, takeUntil, filter } from 'rxjs/operators';

import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import {
   Business, Country, Region,
   BillingParty, BillingPartyGroup,
   Contact,
} from '@library/common/models';

import { ShipmentService } from '../shipment.service';
import { AddressbookService } from '@library/modules/addressbook';
import { EnumsService, Preferences, CountriesService, } from '@library/core/services';

@Component({
  selector: 'billing-party',
  templateUrl: './billing-party.component.html',
  styleUrls: ['./billing-party.component.scss']
})
export class BillingPartyComponent implements OnInit, OnDestroy {

   @Input() party: string;
   get businessForm() {
      return this.shipment.shipmentForm.get( this.party ) as FormGroup;
   }
   port: FormControl;

   config;

   autocompleteOptions: BehaviorSubject<Business[]> = new BehaviorSubject([]);
   contacts: BehaviorSubject<Contact[]> = new BehaviorSubject([]);

   filteredCountries: BehaviorSubject<Country[]> = new BehaviorSubject([]);
   countriesFilter = new FormControl();

   filteredRegions: BehaviorSubject<Region[]> = new BehaviorSubject([]);
   regionsFilter = new FormControl();

   loop = false;

   get pref() { return this.prefs.getPref( 'booking' ) && this.prefs.getPref( 'booking' )[ this.party + '_country' ]; }
   get country() { return this.businessForm.get( 'country' ).value; }
   get state() { return this.businessForm.get( 'state' ).value; }
   get contact() { return this.businessForm.get( 'contact' ) as FormGroup; }

   _onDestroy: Subject<void> = new Subject();
   _clear: Subject<void> = new Subject();

   isQuote = false;

   stateRequired = false;

   get postalValidator() {
      return this.businessForm.get('postalcode').validator({} as AbstractControl)?.required;
   }

   constructor(
      public abService: AddressbookService,
      public enums: EnumsService,
      public prefs: Preferences,
      public shipment: ShipmentService,
      public countries: CountriesService,
   ) { }

   ngOnInit() {
      this.shipment.shipmentUpdate$.pipe(
         tap(_ => this._clear.next()),
         takeUntil(this._onDestroy),
      ).subscribe((updated) => {
         if (updated) {
            const form = this.shipment.shipmentForm;
            this.isQuote = form.get('isQuote').value;

            if (this.isQuote) {
               this.filteredCountries
                  .pipe(
                     map((countries) => countries.map((c) => c.code)),
                     takeUntil(this._onDestroy),
                  ).subscribe((countries) => {
                     const country = this.businessForm.get('country').value;
                     
                     if ( country !== '' && countries.length > 0 && !countries.includes( country ) ) {
                        this.businessForm.get('country').setValue('');
                        this.prefs.upsertPref('booking', { [`${ this.party }_country`]: '' });
                     }
                  });
            }

            this.config = this.shipment.configs?.[this.party];

            if (this.shipment.mode === 'ocean') {
               this.port = form.get(this.party === 'shipper' ? 'origin' : 'destination') as FormControl;
            } else {
               this.port = undefined;
            }

            this.countriesFilter.updateValueAndValidity();
            this.regionsFilter.updateValueAndValidity();

            this.load();

            this.updateValidators();
         }
      });
   }

   ngOnDestroy() {
      this._onDestroy.next();
      this._onDestroy.complete();
   }

   load() {
      // countries check
      // the current country should be in the allowed list of countries for the mode
      this.filteredCountries.pipe(
         filter( _ => !!this.country ),
         filter( countries => !!countries.length ),
         filter( countries => !countries.map( c => c.code ).includes( this.country )),
         takeUntil( this._clear ),
         takeUntil( this._onDestroy ),
      ).subscribe( _ => this.businessForm.get( 'country' ).reset() );

      // regions check
      // the current region should exist in the current country
      this.filteredRegions.pipe(
         filter( _ => !!this.country ),
         filter( _ => !!this.state ),
         filter( regions => !!regions.length ),
         filter( regions => !regions.map( r => r.code ).includes( this.state )),
         takeUntil( this._clear ),
         takeUntil( this._onDestroy ),
      ).subscribe( _ => this.businessForm.get( 'state' ).reset() );

      // load countries from shipType
      this.countriesFilter.valueChanges.pipe(
         startWith( '' ),
         map( value => value?.toLowerCase().trim() ?? '' ),
         switchMap( value => this.countries.countriesByDomesticFactor( this.shipment.shipType ).pipe(
            map( countries => countries.filter( c => c.country.toLowerCase().includes( value ))),
         )),
         takeUntil( this._clear ),
         takeUntil( this._onDestroy ),
      ).subscribe( value => this.filteredCountries.next( value ));

      // load regions
      this.regionsFilter.valueChanges.pipe(
         startWith( '' ),
         filter( _ => !!this.country ),
         map( value => value?.toLowerCase().trim() ?? '' ),
         switchMap( value => this.countries.country( this.country ).pipe(
            map( c => c?.regions ?? [] ),
            map( regions => regions.filter( r => r.region.toLowerCase().includes( value ))),
         )),
         // clear the region if there is no regions
         tap( regions => {
            if ( !regions.length && this.state ) {
               this.businessForm.get( 'state' ).reset( null, { emitEvent: false });
            }
         }),
         takeUntil( this._clear ),
         takeUntil( this._onDestroy ),
      ).subscribe( value => this.filteredRegions.next( value ));

      // get autocomplete list for name
      merge(
         this.shipment.shipmentForm.get( 'shipper' ).get( 'id' ).valueChanges,
         this.shipment.shipmentForm.get( 'consignee' ).get( 'id' ).valueChanges,
         this.businessForm.get( 'name' ).valueChanges,
      ).pipe(
         startWith( '' ),
         map( _ => this.businessForm.get( 'name' ).value ),
         map(( value: string ) => value && ( typeof( value ) === 'string' ) ? value.toLowerCase() : '' ),
         switchMap( value =>
            this.abService.getAddressBook().pipe(
               map( book => book.filter( a => a.name.toLowerCase().includes( value ))),
               map( book => book.filter( a =>
                  [ 'shipper', 'consignee' ].map( p => this.shipment.shipment[p].id || 0 ).indexOf( a.id ) === -1 )),
            )
         ),
         takeUntil( this._clear ),
         takeUntil( this._onDestroy ),
      ).subscribe( filtered => this.autocompleteOptions.next( filtered ));

      // load businessForm logic
      this.businessForm.valueChanges.pipe(
         takeUntil( this._clear ),
         takeUntil( this._onDestroy ),
      ).subscribe( _ => {
         // reset id if anything changed
         if ( this.businessForm.get( 'id' ).value && this.changed() ) {
            this.businessForm.get( 'id' ).reset( null, { emitEvent: false });
         }

         // we're only loading the country pref if it's a blank party
         if ( !this.businessForm.get( 'name' ).value && this.pref && !this.changed() ) {
            this.businessForm.get( 'country' ).setValue( this.pref, { emitEvent: false });
         }

         const country = this.businessForm.get( 'country' ).value;
         if ( country ) {
            // set country pref (maybe)
            if ( !this.pref || ( this.pref && this.pref !== country )) {
               this.prefs.upsertPref( 'booking', {[ this.party + '_country' ]: country });
            }

            this.regionsFilter.updateValueAndValidity();
         }
      });

      // set the postal code validators -- this has to be separate to avoid infinite loop
      this.businessForm.get( 'country' ).valueChanges.pipe(
         startWith( this.businessForm.get( 'country' ).value ),
         filter( value => !!value ),
         takeUntil( this._clear ),
         takeUntil( this._onDestroy ),
      ).subscribe( country => {
         this.countries.setPostalCodeValidators( country, this.businessForm.get( 'postalcode' ), this.isQuote );
         if ( this.isQuote ) {
            this.countries.country( this.country ).subscribe(( active ) => {
               this.businessForm.get('postalcode').addValidators(Validators.required);

               if ( active.regions == null || active.regions.length === 0 ) {
                  this.businessForm.get('state').removeValidators(Validators.required);
                  this.businessForm.get('state').setErrors( null );
                  this.stateRequired = false;
               } else {
                  this.businessForm.get('state').addValidators(Validators.required);
                  this.stateRequired = true;
               }
               this.businessForm.updateValueAndValidity();
            });
         }
      });
   }

   create() {
      this.loop = true;
      const contact_name = this.businessForm.get( 'contact' ).get( 'name' );
      const contact_phone = this.businessForm.get( 'contact' ).get( 'phone' );

      if ( contact_phone.value && !contact_name.value ) {
         this.businessForm.get( 'contact' ).get( 'name' ).setValue( 'Default' );
      }

      const business = new Business( this.businessForm.value ).save();

      this.abService.upsertBusiness( business ).subscribe( b => {
         this.businessForm.get( 'id' ).setValue( b.id, { emitEvent: false });

         this.loop = false;
      });
   }

   updateBusiness( business: MatAutocompleteSelectedEvent ) {
      const businessValue = { 
         ...business.option.value, 
         contact: business.option.value.contacts.find((item: any) => item.default === true) 
      };
      const b: BillingParty = new BillingParty( businessValue );
      // Get the values from BillingPartyGroup
      const formValues = BillingPartyGroup(b).value;
      
      // selectively update only enabled controls
      Object.keys(formValues).forEach(controlName => {
         const control = this.businessForm.get(controlName);
         
         // Only update if the control exists and is enabled
         if (control && !control.disabled) {
            control.setValue(formValues[controlName]);
         }
      });
   }

   updateContact( contact: MatAutocompleteSelectedEvent ) {
      const c = contact.option.value;

      this.businessForm.controls['contact'].setValue({ name: c.name, phone: c.phone });
   }

   changed(): boolean {
      return [ 'name', 'address1', 'address2', 'city', 'state', 'country', 'postalcode' ]
         .filter( v => this.businessForm.controls[v].dirty ).length > 0;
   }

   updateValidators() {
      if ( this.isQuote ) {
         this.shipment.shipmentForm.get(this.party).get('name').removeValidators(Validators.required);
         this.shipment.shipmentForm.get(this.party).get('address1').removeValidators(Validators.required);
         this.shipment.shipmentForm.get(this.party).get('postalcode').addValidators(Validators.required);
         this.shipment.shipmentForm.get(this.party).get('state').addValidators(Validators.required);
         
         this.shipment.shipmentForm.get(this.party).updateValueAndValidity();
      }
   }
}
